import { useQuery } from "@tanstack/react-query";
import { api } from "../../../../singleton/axios";

export interface CashFlowStatementRecord {
  date: string;
  symbol: string;
  reportedCurrency: string;
  cik: string;
  filingDate: string;
  acceptedDate: string;
  fiscalYear: string;
  period: string;
  netIncome: number;
  depreciationAndAmortization: number;
  deferredIncomeTax: number;
  stockBasedCompensation: number;
  changeInWorkingCapital: number;
  accountsReceivables: number;
  inventory: number;
  accountsPayables: number;
  otherWorkingCapital: number;
  otherNonCashItems: number;
  netCashProvidedByOperatingActivities: number;
  investmentsInPropertyPlantAndEquipment: number;
  acquisitionsNet: number;
  purchasesOfInvestments: number;
  salesMaturitiesOfInvestments: number;
  otherInvestingActivities: number;
  netCashProvidedByInvestingActivities: number;
  netDebtIssuance: number;
  longTermNetDebtIssuance: number;
  shortTermNetDebtIssuance: number;
  netStockIssuance: number;
  netCommonStockIssuance: number;
  commonStockIssuance: number;
  commonStockRepurchased: number;
  netPreferredStockIssuance: number;
  netDividendsPaid: number;
  commonDividendsPaid: number;
  preferredDividendsPaid: number;
  otherFinancingActivities: number;
  netCashProvidedByFinancingActivities: number;
  effectOfForexChangesOnCash: number;
  netChangeInCash: number;
  cashAtEndOfPeriod: number;
  cashAtBeginningOfPeriod: number;
  operatingCashFlow: number;
  capitalExpenditure: number;
  freeCashFlow: number;
  incomeTaxesPaid: number;
  interestPaid: number;
}

export function useCompanyCashFlowStatement(ticker: string, limit?: number, period?: 'annual' | 'quarter') {
  return useQuery<CashFlowStatementRecord[], Error>({
    queryKey: ["companyCashFlowStatement", ticker, limit, period],
    queryFn: async () => {
      const params: any = { ticker };
      if (limit) {
        params.limit = limit.toString();
      }
      if (period) {
        params.period = period;
      }

      const { data } = await api.get<CashFlowStatementRecord[]>("/company-cash-flow-statement", {
        params,
      });
      return data;
    },
    enabled: !!ticker,
    refetchOnWindowFocus: false,
    staleTime: 2.5 * 60 * 1000, // 2.5 minutes for fresh data
    retry: (failureCount, error) => {
      // Don't retry on 404s - legitimately means no cash flow data exists
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((error as any)?.response?.status === 404) {
        return false;
      }
      // Retry other errors (500s, network issues) max 2 times
      return failureCount < 2;
    },
  });
}